import { Callout } from "@radix-ui/themes";
import { type ReactNode } from "react";

/**
 * Radix-stock colors only. These pairings are WCAG-AA tuned by Radix Themes,
 * unlike the app's custom `puntt-gold` scale whose text steps fail AA on the
 * light gold surface (gold-9 body = 1.54:1, gold-11 title = 4.35:1).
 *
 * Semantic mapping shared across every Ask Puntt review-status card:
 *   blue   → passive "AI working, wait"
 *   amber  → "your turn, act now" (carries a CTA)
 *   green  → resolved / done
 *   red    → blocked
 *   indigo → new AI plan awaiting approval
 */
export type ReviewStatusColor = "blue" | "amber" | "green" | "red" | "indigo";

interface ReviewStatusCalloutProps {
  color: ReviewStatusColor;
  /**
   * Decorative status icon. Rendered inside `Callout.Icon` and forced
   * `aria-hidden` — the text content always states the status, so the icon
   * is redundant to assistive tech.
   */
  icon?: ReactNode;
  /** Passed through to `Callout.Root` for deterministic targeting. */
  testid: string;
  /** `Callout.Text` (+ optional sibling description / action) for this state. */
  children: ReactNode;
  /** Consumer spacing hooks (e.g. `mb-5` in the status pane). */
  className?: string;
}

/**
 * Shared, accessible card shell for Ask Puntt review-status messaging. Wraps
 * Radix Themes `Callout` and hardcodes a polite live region (`role="status"`)
 * so screen-reader users hear the review progression announced in place.
 *
 * The shell intentionally owns ONLY the colored container + icon + role. Action
 * buttons stay with each consumer (the status banner has one CTA; the status
 * pane has a two-button row), so this never grows into a god-props component.
 */
export function ReviewStatusCallout({
  color,
  icon,
  testid,
  children,
  className,
}: ReviewStatusCalloutProps) {
  return (
    <Callout.Root
      color={color}
      size="1"
      role="status"
      aria-live="polite"
      data-testid={testid}
      className={className}
    >
      {icon != null && <Callout.Icon aria-hidden="true">{icon}</Callout.Icon>}
      {children}
    </Callout.Root>
  );
}
